import config from '@/config'
import { PageRequest, PageResponse, SortDirection } from '@/types/Support'
import api from '@/api'
import moment from 'moment'
import { CatalogGridItemColumn, Client, ClientCatalogFilters, ClientWorkspacesFilters, Domain, DomainCatalogFilters, Workspace, Permission, PermissionCatalogFilters, RoleCatalogFilters, Role, RoleUpdate, PermissionUpdate, WorkspaceStatus, UserCatalogFilters, User, BasicEntity, MapProvider, FullTextSearchType } from '@/types/Catalogs'
import { ProjectCatalogFilters, Project } from '@/types/catalogs/Projects'
import { GroupCatalogFilters, GroupEntity, GroupStatus } from '@/types/catalogs/Groups'
import { Device, DeviceCatalogFilters } from '@/types/catalogs/Devices'
import { AxiosResponse } from 'axios'
import { License, LicenseFilters } from '@/types/catalogs/Licenses'

function setRequestIdInData (response: AxiosResponse<any>) {
  response.data['header-request-id'] = response.headers['request-id'] ? response.headers['request-id'] : `Sin identificador`
}

class CatalogsService {
  /* CLIENTS ***********************************************/

  async getClients (pageRequest: PageRequest<ClientCatalogFilters>): Promise<PageResponse<Client>> {
    const response = await api.get(
      `${config.apiUsersUrl}clients?limit=${pageRequest.size}&offset=${pageRequest.page * pageRequest.size}${pageRequest.sort ? '&sort=' + pageRequest.sort : ''}${pageRequest.filters.status ? '&status=' + pageRequest.filters.status : ''}`
    )
    return response.data
  }

  async getClientById (id: number): Promise<Client> {
    const response = await api.get(
      `${config.apiUsersUrl}clients/${id}`
    )
    return response.data
  }

  async saveClient (client: Client): Promise<any> {
    const request = {
      name: client.name,
      clientCode: client.clientCode,
      rfc: client.rfc.toUpperCase(),
      juridicalPerson: client.juridicalPerson,
      stpClabe: client.stpClabe !== '' ? client.stpClabe : null,
      contact: {
        contactName: client.contact.contactName,
        email: client.contact.email,
        phone: client.contact.phone,
        emailExtra: client.contact.emailExtra,
        phoneExtra: client.contact.phoneExtra
      },
      address: {
        streetAddress: client.address.streetAddress,
        extNumAddress: client.address.extNumAddress,
        intNumAddress: client.address.intNumAddress,
        country: client.address.country,
        district: client.address.district,
        delegation: client.address.delegation,
        state: client.address.state,
        zipcode: client.address.zipcode
      },
      comercialCharge: client.comercialCharge,
      minimumLicenses: client.minimumLicenses,
      startDate: client.startDate ? moment(client.startDate).utc().format('YYYY-MM-DDT00:00:00') : null,
      status: client.status
    }
    const response = client.id ? await api.put(`${config.apiUsersUrl}clients/${client.id}`, request) : await api.post(`${config.apiUsersUrl}clients`, request)
    setRequestIdInData(response)
    return response.data
  }

  /* CLIENTS - WORKSPACES ***********************************/

  async getWorkspacesAll (pageRequest: PageRequest<ClientWorkspacesFilters>): Promise<PageResponse<Workspace>> {
    const response = await api.get(
      `${config.apiUsersUrl}workspaces?limit=${pageRequest.size}&offset=${pageRequest.page * pageRequest.size}${pageRequest.sort ? '&sort=' + pageRequest.sort : ''}`
    )
    return response.data
  }

  async getWorkspacesByClients (pageRequest: PageRequest<ClientWorkspacesFilters>): Promise<PageResponse<Workspace>> {
    const response = await api.get(
      `${config.apiUsersUrl}workspaces?limit=${pageRequest.size}&offset=${pageRequest.page * pageRequest.size}${pageRequest.sort ? '&sort=' + pageRequest.sort : ''}${'&client=' + pageRequest.filters.clientId}`
    )
    return response.data
  }

  async getWorkspacesByDomain (pageRequest: PageRequest<ClientWorkspacesFilters>): Promise<PageResponse<Workspace>> {
    const response = await api.get(
      `${config.apiUsersUrl}workspaces?limit=${pageRequest.size}&offset=${pageRequest.page * pageRequest.size}${pageRequest.sort ? '&sort=' + pageRequest.sort : ''}${'&domain=' + pageRequest.filters.domainId}`
    )
    return response.data
  }

  async getWorkspacesByClient (pageRequest: PageRequest<ClientWorkspacesFilters>): Promise<PageResponse<Workspace>> {
    const response = await api.get(
      `${config.apiUsersUrl}workspaces?limit=${pageRequest.size}&offset=${pageRequest.page * pageRequest.size}${pageRequest.sort ? '&sort=' + pageRequest.sort : ''}${'&client=' + pageRequest.filters.clientId}`
    )
    return response.data
  }

  async saveWorkspace (workspace: Workspace): Promise<any> {
    const request = {
      status: workspace.status,
      domain: workspace.domain.id,
      client: workspace.client.id,
      owner: workspace.owner || false,
      consolidatedInvoice: workspace.consolidatedInvoice,
      facturationDate: workspace.facturationDate,
      contactName: workspace.contact.name,
      contactTelephone: workspace.contact.contactTelephone,
      contactEmail: workspace.contact.contactEmail
    }

    const response = workspace.id ? await api.put(`${config.apiUsersUrl}workspaces/${workspace.id}`, request) : await api.post(`${config.apiUsersUrl}workspaces`, request)
    setRequestIdInData(response)
    return response.data
  }

  async saveWorkspaceOwner (id: number, isOwner: boolean): Promise<any> {
    const request = {
      owner: isOwner
    }
    const response = await api.patch(`${config.apiUsersUrl}workspaces/${id}`, request)
    return response.data
  }

  async saveWorkspaceStatus (id: number, status: WorkspaceStatus): Promise<any> {
    const request = {
      status: status
    }
    const response = await api.patch(`${config.apiUsersUrl}workspaces/${id}`, request)
    return response.data
  }

  async inactiveWorkspace (id: number): Promise<any> {
    const response = await api.delete(`${config.apiUsersUrl}workspaces/${id}`,
      {
        headers:
        {
          'Content-Type': 'application/json'
        },
        data: null
      })
    return response.data
  }

  /* DOMAINS ************************************************/

  async getDomains (pageRequest: PageRequest<DomainCatalogFilters>): Promise<PageResponse<Domain>> {
    let request = `${config.apiUsersUrl}domains`
    request += `?limit=${pageRequest.size}&offset=${pageRequest.page * pageRequest.size}`
    request += `${pageRequest.sort ? '&sort=' + pageRequest.sort : ''}`
    request += `${pageRequest.filters.loadRelations ? '&load-relations=true' : ''}`
    request += `${pageRequest.filters.domainType ? '&domainType=' + pageRequest.filters.domainType : ''}`
    const response = await api.get(request)
    return response.data
  }

  async getDomainById (id: number): Promise<Domain> {
    const response = await api.get(
      `${config.apiUsersUrl}domains/${id}`
    )
    return response.data
  }

  async saveDomain (domain: Domain): Promise<any> {
    const request = {
      name: domain.name,
      securityLevel: domain.securityLevel,
      webBMFMinimumRevision: domain.webBMFMinimumRevision,
      broadcast: domain.broadcast !== '' ? domain.broadcast : null,
      usePredictiveDialerOpt: domain.usePredictiveDialerOpt,
      limitLicenses: domain.limitLicenses,
      mapProvider: domain.mapProvider !== null && domain.mapProvider !== undefined && domain.mapProvider !== '' ? domain.mapProvider : MapProvider.MAPBOX,
      fullTextSearchType: domain.fullTextSearchType !== null && domain.fullTextSearchType !== undefined ? domain.fullTextSearchType : FullTextSearchType.ELASTIC,
      rateLimitTime: domain.rateLimitTime !== '' as any ? domain.rateLimitTime : -1,
      rateLimitWS: domain.rateLimitWS !== '' as any ? domain.rateLimitWS : -1,
      trackingMode: domain.trackingMode !== null && domain.trackingMode !== undefined && domain.trackingMode !== '' ? domain.trackingMode : null,
      trackerVersion: domain.trackerVersion, // this always will be a default value (2)
      adminInfo: {
        billingCode: domain.billingCode,
        domainType: domain.domainType,
        productLine: domain.productLine,
        businessSegment: domain.businessSegment
      }
    }
    const response = domain.id ? await api.put(`${config.apiUsersUrl}domains/${domain.id}`, request) : await api.post(`${config.apiUsersUrl}domains`, request)
    setRequestIdInData(response)
    return response.data
  }

  /* PERMISSIONS ***********************************************/
  async getPermissions (pageRequest: PageRequest<PermissionCatalogFilters>): Promise<PageResponse<Permission>> {
    let requestBase = `${config.apiUsersUrl}permissions`
    requestBase += `?limit=${pageRequest.size}`
    requestBase += `&offset=${pageRequest.page * pageRequest.size}`
    requestBase += `${pageRequest.sort ? '&sort=' + pageRequest.sort : ''}`
    requestBase += `${pageRequest.filters.domain ? '&domain=' + pageRequest.filters.domain : ''}`
    requestBase += `${pageRequest.filters.loadRelations ? '&load-relations=' + pageRequest.filters.loadRelations : ''}`
    requestBase += `${pageRequest.filters.resource ? '&resource=' + pageRequest.filters.resource : ''}`
    const response = await api.get(requestBase)
    return response.data
  }

  async getPermissionsById (id: number): Promise<Permission> {
    const response = await api.get(
      `${config.apiUsersUrl}permissions/${id}`
    )
    return response.data
  }

  async savePermission (perm: PermissionUpdate): Promise<any> {
    const request = {
      name: perm.name,
      domain: perm.domain,
      resource: perm.resource,
      effect: perm.effect,
      label: perm.label,
      version: perm.version,
      description: perm.description,
      roles: perm.roles
    }
    const urlResource = `${config.apiUsersUrl}permissions`
    const response = perm.id ? await api.put(`${urlResource}/${perm.id}`, request) : await api.post(`${urlResource}`, request)
    return response.data
  }

  async deletePermissionById (id: number): Promise<any> {
    const response = api.delete(`${config.apiUsersUrl}permissions/${id}`)
    return response
  }

  /** ROLES */
  async getRoles (pageRequest: PageRequest<RoleCatalogFilters>): Promise<PageResponse<Role>> {
    let request = `${config.apiUsersUrl}roles?`
    request += `limit=${pageRequest.size}`
    request += `&offset=${pageRequest.page * pageRequest.size}`
    request += `${pageRequest.sort ? '&sort=' + pageRequest.sort : ''}`
    request += `${pageRequest.filters.domain >= 0 ? '&domain=' + pageRequest.filters.domain : ''}`
    const response = await api.get(request)
    return response.data
  }

  async getRoleById (id: number): Promise<Role> {
    const response = await api.get(
      `${config.apiUsersUrl}roles/${id}`
    )
    return response.data
  }

  async saveRole (role: RoleUpdate): Promise<any> {
    const request = {
      name: role.name,
      copyFromRole: role.copyFromRole === null || role.copyFromRole === '' ? null : role.copyFromRole,
      maxSessions: role.maxSessions,
      domain: role.domain,
      permissions: role.permissions
    }
    const urlResource = `${config.apiUsersUrl}roles`
    const response = role.id ? await api.put(`${urlResource}/${role.id}`, request) : await api.post(`${urlResource}`, request)
    return response.data
  }

  async deleteRoleById (id: number): Promise<any> {
    const response = api.delete(`${config.apiUsersUrl}roles/${id}`)
    return response
  }

  /* USERS ************************************************/

  async getUsers (pageRequest: PageRequest<UserCatalogFilters>): Promise<PageResponse<User>> {
    const filters = pageRequest.filters
    let url = `${config.apiUsersUrl}users?limit=${pageRequest.size}`
    url += `&offset=${pageRequest.page * pageRequest.size}`
    url += `${pageRequest.sort ? '&sort=' + pageRequest.sort : ''}`
    url += `${pageRequest.sortOrder ? '&sort-order=' + pageRequest.sortOrder : ''}`
    url += `${filters.loadRelations ? '&load-relations=true' : ''}`
    url += `${filters.domainId >= 0 ? '&domain=' + filters.domainId : ''}`
    url += `${filters.name ? '&name=' + filters.name : ''}`
    url += `${filters.clientId && filters.clientId > 0 ? '&client=' + filters.clientId : ''}`
    url += `${filters.roleId && filters.roleId > 0 ? '&role=' + filters.roleId : ''}`
    url += `${filters.roleId && filters.roleId > 0 && filters.copyFromRole ? '&copy-from-role=' + filters.copyFromRole : ''}`
    url += `${filters.groupId && filters.groupId > 0 ? '&group=' + filters.groupId : ''}`
    url += `${filters.groupId && filters.groupId > 0 && filters.subGroup ? '&sub-groups=' + filters.subGroup : ''}`
    url += `${filters.simpleStatus ? '&status=' + filters.simpleStatus : ''}`
    url += `${filters.billable ? '&billable=' + filters.billable : ''}`
    if (filters.licenseType && filters.fromTo && filters.fromTo.length === 2) {
      url += `${'&license-type=' + filters.licenseType}`
      url += `${'&from=' + moment(filters.fromTo[0]).format('YYYY-MM-DD')}`
      url += `${'&to=' + moment(filters.fromTo[1]).format('YYYY-MM-DD')}`
    }
    const response = await api.get(url)
    return response.data
  }

  async getUserById (id: number): Promise<User> {
    const response = await api.get(
      `${config.apiUsersUrl}users/${id}`
    )
    return response.data
  }

  async saveUser (user: User): Promise<any> {
    const request = {
      name: user.name,
      status: user.status,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      emailExtra: user.emailExtra,
      billable: user.billable,
      extendedPermissions: user.extendedPermissions,
      phoneExtra: user.phoneExtra || null,
      cellPhone: {
        countryCode: user.cellPhone.countryCode || null,
        number: user.cellPhone.number || null
      },
      client: user.client.id,
      domain: user.domain.id,
      role: user.role.id,
      groups: user.groups.reduce((arr: number[], it: BasicEntity) => arr.concat(it.id), [])
    }

    const response = user.id ? await api.put(`${config.apiUsersUrl}users/${user.id}`, request) : await api.post(`${config.apiUsersUrl}users`, request)
    setRequestIdInData(response)
    return response.data
  }

  async inactiveUser (userId: number): Promise<any> {
    const url = `${config.apiUsersUrl}users/${userId}`
    const urlConfig = { headers: { 'Content-Type': 'application/json' }, data: null }
    const response = await api.delete(url, urlConfig)
    return response.data
  }

  async resetUserPassword (userId: number): Promise<any> {
    const resetUrl = `${config.apiUsersUrl}users/${userId}/reset-password`
    const response = await api.post(resetUrl,
      {
        headers:
        {
          'Content-Type': 'application/json'
        },
        data: null
      }
    )
    return response.data
  }

  /* GRUPOS ************************************************/

  async getGroups (pageRequest: PageRequest<GroupCatalogFilters>): Promise<PageResponse<GroupEntity>> {
    const filters = pageRequest.filters
    let request = `${config.apiUsersUrl}groups?`
    request += `${pageRequest.sort ? 'sort=' + pageRequest.sort : 'sort=id'}`
    request += `${filters.domain >= 0 ? '&domain=' + filters.domain : ''}`
    request += `${filters.loadRelations ? '&load-relations=' + filters.loadRelations : ''}`
    request += `${filters.status === GroupStatus.ACTIVE ? '&status=' + filters.status : ''}`
    if (filters.status === GroupStatus.ACTIVE && filters.fromToDates && filters.fromToDates.length === 2) {
      const from = filters.fromToDates[0]
      const to = filters.fromToDates[1]
      const fromWithFormat = new Intl.DateTimeFormat('fr-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(from)
      const toWithFormat = new Intl.DateTimeFormat('fr-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(to)
      request += `&from=${fromWithFormat}`
      request += `&to=${toWithFormat}`
    }
    const response = await api.get(request)
    return response.data
  }

  async getGroupById (id: number): Promise<GroupEntity> {
    const response = await api.get(
      `${config.apiUsersUrl}groups/${id}`
    )
    return response.data
  }

  async getGroupsByDomain (domainId: number) {
    const url = `${config.apiUsersUrl}groups?sort=name&domain=${domainId}`
    const response = await api.get(url)
    return response.data
  }

  async saveGroup (group: GroupEntity): Promise<GroupEntity> {
    const request = {
      name: group.name,
      domain: group.domain.id,
      subGroups: group.subGroups
        .reduce((arr: number[], it: GroupEntity) =>
          (it.id > 0) ? arr.concat(it.id) : arr.concat([]), []),
      users: group.users
        .reduce((arr: number[], it: BasicEntity) =>
          (it.id > 0) ? arr.concat(it.id) : arr.concat([]), []),
      projects: group.projects
        .reduce((arr: number[], it: BasicEntity) =>
          (it.id > 0) ? arr.concat(it.id) : arr.concat([]), [])
    }
    const url = `${config.apiUsersUrl}groups`
    const response = group.id ? await api.put(`${url}/${group.id}`, request) : await api.post(`${url}`, request)
    setRequestIdInData(response)
    return response.data
  }

  async deleteGroup (groupId: number) {
    const request = `${config.apiUsersUrl}groups/${groupId}`
    const response = await api.delete(request,
      { headers: { 'Content-Type': 'application/json' }, data: null })
    return response
  }

  /** PROJECTS ************************************************/

  async getProjects (pageRequest: PageRequest<ProjectCatalogFilters>): Promise<PageResponse<Project>> {
    let request = `${config.apiUsersUrl}projects`
    const filters = pageRequest.filters
    request += `?limit=${pageRequest.size}`
    request += `&offset=${pageRequest.page * pageRequest.size}`
    request += `${pageRequest.sort ? '&sort=' + pageRequest.sort : ''}`
    request += `${filters.enable !== undefined ? '&enable=' + filters.enable : ''}`
    request += `${filters.group ? '&group=' + filters.group : ''}`
    request += `${filters.deepSearch ? '&deep-search=' + filters.deepSearch : ''}`
    request += `${filters.domain >= 0 ? '&domain=' + filters.domain : ''}`
    request += `${filters.loadRelations ? '&load-relations=' + filters.loadRelations : ''}`
    const response = await api.get(request)
    return response.data
  }

  async getProjectById (id: number): Promise<Project> {
    const response = await api.get(
      `${config.apiUsersUrl}projects/${id}`
    )
    return response.data
  }

  async saveProject (project: Project): Promise<Project> {
    const request = {
      name: project.name,
      domain: project.domain.id,
      enable: project.enable,
      groups: project.groups.reduce((arr: number[], it: BasicEntity) => arr.concat(it.id), [])
    }
    const urlResource = `${config.apiUsersUrl}projects`
    const response = project.id ? await api.put(`${urlResource}/${project.id}`, request) : await api.post(`${urlResource}`, request)
    return response.data
  }

  /* DEVICES ************************************************/

  async getDevices (pageRequest: PageRequest<DeviceCatalogFilters>): Promise<PageResponse<Device>> {
    let request = `${config.apiUsersUrl}devices`
    request += `?limit=${pageRequest.size}`
    request += `&offset=${pageRequest.page * pageRequest.size}`
    request += `${pageRequest.sort ? '&sort=' + pageRequest.sort : ''}`
    request += `${pageRequest.sortOrder ? '&sort-order=' + pageRequest.sortOrder : ''}`
    request += `${pageRequest.filters.name !== undefined && pageRequest.filters.name !== '' ? '&name=' + pageRequest.filters.name : ''}`
    request += `${pageRequest.filters.uuid !== undefined && pageRequest.filters.uuid !== '' ? '&uuid=' + pageRequest.filters.uuid : ''}`
    request += `${pageRequest.filters.status !== undefined ? '&status=' + pageRequest.filters.status : ''}`
    const response = await api.get(request)
    return response.data
  }

  async getDeviceById (id: number): Promise<Device> {
    const response = await api.get(
      `${config.apiUsersUrl}devices/${id}`
    )
    return response.data
  }

  async deleteDeviceById (id: number): Promise<Device> {
    const response = await api.delete(`${config.apiUsersUrl}devices/${id}`,
      {
        headers:
        {
          'Content-Type': 'application/json'
        },
        data: null
      }
    )
    return response.data
  }

  /* LICENSES ***********************************************/

  async getLicenses (pageRequest: PageRequest<LicenseFilters>): Promise<PageResponse<License>> {
    let request = `${config.apiUsersUrl}licenses`
    request += `?limit=${pageRequest.size}`
    request += `&offset=${pageRequest.page * pageRequest.size}`
    request += `${pageRequest.sortOrder === SortDirection.DESC ? '&sort-order=' + pageRequest.sortOrder : ''}`
    request += `${pageRequest.sort ? '&sort=' + pageRequest.sort : ''}`
    request += `${pageRequest.filters.domain >= 0 ? '&domain=' + pageRequest.filters.domain : ''}`
    request += `${pageRequest.filters.userId > 0 ? '&user=' + pageRequest.filters.userId : ''}`
    request += `${pageRequest.filters.deviceId > 0 ? '&device=' + pageRequest.filters.deviceId : ''}`
    request += `${pageRequest.filters.active ? '&active=' + pageRequest.filters.active : ''}`
    const response = await api.get(
      request,
      {
        headers: {
          'Access-Control-Max-Age': 600,
          'Cache-Control': 'public, max-age=600'
        }
      }
    )
    return response.data
  }

  async getLicense (id: number): Promise<License> {
    const response = await api.get(
      `${config.apiUsersUrl}licenses/${id}`
    )
    return response.data
  }

  async deleteLicense (id: number): Promise<License> {
    const response = await api.delete(
      `${config.apiUsersUrl}licenses/${id}`,
      {
        headers:
        {
          'Content-Type': 'application/json'
        },
        data: null
      }
    )
    return response.data
  }

  /* GENERICS ***********************************************/

  getCountries (): string[] {
    return ['Afganistán', 'Albania', 'Alemania', 'Andorra', 'Angola', 'Antigua y Barbuda', 'Arabia Saudita', 'Argelia', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaiyán', 'Bahamas', 'Bangladés', 'Barbados', 'Baréin', 'Bélgica', 'Belice', 'Benín', 'Bielorrusia', 'Birmania', 'Bolivia', 'Bosnia y Herzegovina', 'Botsuana', 'Brasil', 'Brunéi', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Bután', 'Cabo Verde', 'Camboya', 'Camerún', 'Canadá', 'Catar', 'Chad', 'Chile', 'China', 'Chipre', 'Ciudad del Vaticano', 'Colombia', 'Comoras', 'Corea del Norte', 'Corea del Sur', 'Costa de Marfil', 'Costa Rica', 'Croacia', 'Cuba', 'Dinamarca', 'Dominica', 'Ecuador', 'Egipto', 'El Salvador', 'Emiratos Árabes Unidos', 'Eritrea', 'Eslovaquia', 'Eslovenia', 'España', 'Estados Unidos', 'Estonia', 'Etiopía', 'Filipinas', 'Finlandia', 'Fiyi', 'Francia', 'Gabón', 'Gambia', 'Georgia', 'Ghana', 'Granada', 'Grecia', 'Guatemala', 'Guyana', 'Guinea', 'Guinea ecuatorial', 'Guinea-Bisáu', 'Haití', 'Honduras', 'Hungría', 'India', 'Indonesia', 'Irak', 'Irán', 'Irlanda', 'Islandia', 'Islas Marshall', 'Islas Salomón', 'Israel', 'Italia', 'Jamaica', 'Japón', 'Jordania', 'Kazajistán', 'Kenia', 'Kirguistán', 'Kiribati', 'Kuwait', 'Laos', 'Lesoto', 'Letonia', 'Líbano', 'Liberia', 'Libia', 'Liechtenstein', 'Lituania', 'Luxemburgo', 'Madagascar', 'Malasia', 'Malaui', 'Maldivas', 'Malí', 'Malta', 'Marruecos', 'Mauricio', 'Mauritania', 'México', 'Micronesia', 'Moldavia', 'Mónaco', 'Mongolia', 'Montenegro', 'Mozambique', 'Namibia', 'Nauru', 'Nepal', 'Nicaragua', 'Níger', 'Nigeria', 'Noruega', 'Nueva Zelanda', 'Omán', 'Países Bajos', 'Pakistán', 'Palaos', 'Panamá', 'Papúa Nueva Guinea', 'Paraguay', 'Perú', 'Polonia', 'Portugal', 'Reino Unido', 'República Centroafricana', 'República Checa', 'República de Macedonia', 'República del Congo', 'República Democrática del Congo', 'República Dominicana', 'República Sudafricana', 'Ruanda', 'Rumanía', 'Rusia', 'Samoa', 'San Cristóbal y Nieves', 'San Marino', 'San Vicente y las Granadinas', 'Santa Lucía', 'Santo Tomé y Príncipe', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leona', 'Singapur', 'Siria', 'Somalia', 'Sri Lanka', 'Suazilandia', 'Sudán', 'Sudán del Sur', 'Suecia', 'Suiza', 'Surinam', 'Tailandia', 'Tanzania', 'Tayikistán', 'Timor Oriental', 'Togo', 'Tonga', 'Trinidad y Tobago', 'Túnez', 'Turkmenistán', 'Turquía', 'Tuvalu', 'Ucrania', 'Uganda', 'Uruguay', 'Uzbekistán', 'Vanuatu', 'Venezuela', 'Vietnam', 'Yemen', 'Yibuti', 'Zambia', 'Zimbabue']
  }

  getMexicoStates () {
    return ['Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas', 'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima', 'Durango', 'Estado de México', 'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas']
  }

  sortItems (items: any[], col: CatalogGridItemColumn) {
    col.dir = col.dir === 'asc' ? 'desc' : 'asc'
    if (col.dir === 'asc') items.sort((a: any, b: any) => (a[col.key] > b[col.key]) ? 1 : -1)
    else items.sort((a: any, b: any) => (a[col.key] < b[col.key]) ? 1 : -1)
  }
}

export default new CatalogsService()
